import { Box, useTheme } from "@mui/material";
import ReactECharts from "echarts-for-react";
import { useMemo } from "react";
import type { GasRow } from "../models/GasConsumption";

interface Props {
	rows: GasRow[];
}

export const GasConsumptionChart = ({ rows }: Props) => {
	const theme = useTheme();
	const barColor = theme.palette.primary.main;

	const option = useMemo(() => {
		const dates = rows.map((r) => r.Date);
		const values = rows.map((r) => (r.Value == null ? null : Number(r.Value)));

		return {
			animation: true,
			animationDuration: 0,
			animationDurationUpdate: 400,
			tooltip: {
				trigger: "axis" as const,
				valueFormatter: (v: number | null) =>
					v == null
						? "—"
						: new Intl.NumberFormat("en-US", {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2,
							}).format(Number(v)),
			},
			grid: { left: 60, right: 24, top: 20, bottom: 64 },
			xAxis: {
				type: "category" as const,
				data: dates,
				axisLabel: { fontSize: 10, rotate: 45, hideOverlap: true },
			},
			yAxis: {
				type: "value" as const,
				axisLabel: {
					formatter: (v: number) =>
						v >= 10000 ? `${(v / 1000).toFixed(0)}k` : String(v),
				},
			},
			series: [
				{
					name: "Gas Consumption",
					type: "bar" as const,
					data: values,
					itemStyle: { color: barColor },
					barMaxWidth: 48,
				},
			],
		};
	}, [rows, barColor]);

	return (
		<Box sx={{ width: "100%", height: "100%" }}>
			<ReactECharts
				option={option}
				notMerge={true}
				lazyUpdate
				style={{ width: "100%", height: "100%" }}
			/>
		</Box>
	);
};
